import { FCWithChildren } from '@types';
import React, { useEffect, useRef } from 'react';

import { ISupportQuestionsAndAnswers } from 'sdk-apogee';

import { Collapsible } from '@components';
import { getParsedHtml } from '@utils';
import ReactHtmlParser from 'react-html-parser';
import { renderToStaticMarkup } from 'react-dom/server';

type QuestionsListProps = {
  questionsAndAnswers: ISupportQuestionsAndAnswers[];
  searchTerm?: string | undefined;
  expandQuestion?: string;
};

const QuestionsList: FCWithChildren<QuestionsListProps> = ({
  questionsAndAnswers,
  searchTerm,
  expandQuestion,
}) => {
  const expandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expandQuestion && expandRef.current) {
      expandRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [expandQuestion]);

  const getHighlightedText = (text: string) => {
    if (searchTerm) {
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = text.split(new RegExp(`(${escapedSearchTerm})`, 'gi'));
      const matchCount = parts.length;
      const highlightedContent = parts.map((part) =>
        part.toLowerCase() === searchTerm.toLowerCase()
          ? renderToStaticMarkup(<span className="bg-yellow-200">{part}</span>)
          : part,
      );
      return { highlightedContent: highlightedContent.join(''), matchCount: matchCount };
    } else {
      return { highlightedContent: text, matchCount: 2 };
    }
  };

  const searchAndHighLightContent = (
    id: string,
    question: string,
    answerHtml: string,
    showContentByDefault: boolean,
  ) => {
    const { highlightedContent: highlightedQuestion, matchCount: questionMatchCount } =
      getHighlightedText(question);
    const { highlightedContent: highLightedAnswer, matchCount: answerMatchCount } =
      getHighlightedText(answerHtml);

    return questionMatchCount > 1 || answerMatchCount > 1 ? (
      <Collapsible
        key={id}
        title={ReactHtmlParser(highlightedQuestion)}
        wide
        showContentByDefault={showContentByDefault}
      >
        <ul className="list-disc py-2">
          <div>{getParsedHtml(highLightedAnswer)}</div>
        </ul>
      </Collapsible>
    ) : null;
  };

  return (
    <>
      {questionsAndAnswers.map(({ id, question, answerHtml }) => {
        const isExpanded = question === expandQuestion;
        const content = searchAndHighLightContent(id, question, answerHtml || '', isExpanded);
        if (!content) return null;
        if (isExpanded) {
          return (
            <div
              key={id}
              ref={expandRef}
              style={{
                borderLeft: '4px solid #1492ff',
                borderRadius: '0 8px 8px 0',
                backgroundColor: 'rgba(20, 146, 255, 0.07)',
                paddingLeft: '10px',
                transition: 'background-color 200ms ease',
              }}
            >
              {content}
            </div>
          );
        }
        return content;
      })}
    </>
  );
};

export default QuestionsList;
